import type {
  ShiftbookEntryWorkspaceEntryPayload,
  ShiftbookEntryWorkspaceSaveRequest,
  ShiftbookEntryWorkspaceSaveResponse,
} from '@blms/shared/schemas/shiftbook';
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession } from '@/features/auth/api/client';
import {
  expireSession,
  getSessionEpoch,
  getSessionGeneration,
  getSessionOwner,
  isSessionVerified,
  ObsoleteSessionError,
  sameSessionUser,
  sessionOwner,
} from '@/features/auth/session-boundary';
import { ApiError } from '@/lib/api-error';

import { AttachmentReadError } from '../api/attachment-upload';
import { fetchEntryDetail, saveEntryWorkspace } from '../api/client';
import { loadStagedFile } from '../offline/entry-workspace-store';
import {
  ENTRIES_QUERY_KEY,
  ENTRY_ATTACHMENTS_QUERY_KEY,
  ENTRY_DETAIL_QUERY_KEY,
  ENTRY_WORKSPACE_SAVE_MUTATION_KEY,
  KEYWORD_LOOKUP_QUERY_KEY,
} from './query-keys';

type UpdateEntryPayloadInput = Omit<
  Extract<ShiftbookEntryWorkspaceEntryPayload, { mode: 'update' }>,
  'version'
> & {
  version?: number;
};

type EntryWorkspaceEntryPayloadInput =
  | Extract<ShiftbookEntryWorkspaceEntryPayload, { mode: 'create' }>
  | UpdateEntryPayloadInput;

export type EntryWorkspaceSaveRequestInput = Omit<ShiftbookEntryWorkspaceSaveRequest, 'entry'> & {
  entry: EntryWorkspaceEntryPayloadInput;
};

export type EntryWorkspaceSaveInput = {
  owner?: string;
  request: EntryWorkspaceSaveRequestInput;
  // Serializable references so offline mutation persistence works.
  fileClientIds: string[];
  displayHeading: string | null;
};

async function resolveSaveRequest(
  request: EntryWorkspaceSaveRequestInput,
): Promise<ShiftbookEntryWorkspaceSaveRequest> {
  if (request.entry.mode !== 'update') {
    return request as ShiftbookEntryWorkspaceSaveRequest;
  }

  const explicitVersion = request.entry.version;
  if (typeof explicitVersion === 'number' && explicitVersion > 0) {
    return {
      ...request,
      entry: {
        ...request.entry,
        version: explicitVersion,
      },
    };
  }

  // Resolve optimistic concurrency version at execution time. This lets offline
  // queued saves be staged even when detail wasn't loaded before disconnect.
  const latestDetail = await fetchEntryDetail(request.entry.entryId);
  return {
    ...request,
    entry: {
      ...request.entry,
      version: latestDetail.version,
    },
  };
}

const saveCompletions = new WeakMap<
  ShiftbookEntryWorkspaceSaveResponse,
  { owner: string; generation: number }
>();

async function executeEntryWorkspaceSave({
  request,
  fileClientIds,
  owner,
}: EntryWorkspaceSaveInput): Promise<ShiftbookEntryWorkspaceSaveResponse> {
  if (!owner || !sameSessionUser(owner, getSessionOwner())) throw new ObsoleteSessionError();
  if (!isSessionVerified()) throw new ApiError(401, 'Session expired');
  const generation = getSessionGeneration();
  const epoch = getSessionEpoch();
  const session = await fetchAuthSession();
  if (epoch !== getSessionEpoch()) throw new ObsoleteSessionError();
  if (!session.authenticated) {
    expireSession();
    throw new ApiError(401, 'Session expired');
  }
  if (!sameSessionUser(sessionOwner(session), owner)) {
    expireSession();
    throw new ObsoleteSessionError();
  }
  const files = await Promise.all(
    fileClientIds.map(async (clientId) => {
      const record = await loadStagedFile(clientId, owner);
      if (!record) {
        const attachment = request.attachments.add.find((item) => item.clientId === clientId);
        throw new AttachmentReadError(attachment?.filename ?? clientId, clientId);
      }
      return {
        clientId,
        blob: record.blob,
        filename: record.filename,
        sizeBytes: record.sizeBytes,
      };
    }),
  );

  const resolvedRequest = await resolveSaveRequest(request);
  if (generation !== getSessionGeneration() || !sameSessionUser(owner, getSessionOwner()))
    throw new ObsoleteSessionError();
  const result = await saveEntryWorkspace({ request: resolvedRequest, files });
  if (generation !== getSessionGeneration() || !sameSessionUser(owner, getSessionOwner()))
    throw new ObsoleteSessionError();
  saveCompletions.set(result, { owner, generation });
  return result;
}

function buildEntryWorkspaceSaveHandlers(queryClient: QueryClient) {
  return {
    mutationFn: executeEntryWorkspaceSave,
    onSuccess: (response: ShiftbookEntryWorkspaceSaveResponse) => {
      const completion = saveCompletions.get(response);
      if (
        !completion ||
        !sameSessionUser(completion.owner, getSessionOwner()) ||
        completion.generation !== getSessionGeneration()
      ) {
        // Throw before cache writes and before TanStack invokes caller success callbacks.
        throw new ObsoleteSessionError();
      }
      // Keep detail and attachment caches in sync with the save response so the
      // open dialog can render immediately without waiting for a round-trip refetch.
      queryClient.setQueryData([ENTRY_DETAIL_QUERY_KEY, response.entryId], response.entryDetail);
      queryClient.setQueryData(
        [ENTRY_ATTACHMENTS_QUERY_KEY, response.entryId],
        response.entryDetail.attachments,
      );

      // Refresh list data, but avoid an immediate active refetch for the detail
      // query we just updated. That prevents post-save loading flicker.
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: [ENTRIES_QUERY_KEY] }),
        queryClient.invalidateQueries({
          queryKey: [ENTRY_DETAIL_QUERY_KEY, response.entryId],
          refetchType: 'none',
        }),
        queryClient.invalidateQueries({
          queryKey: [ENTRY_ATTACHMENTS_QUERY_KEY, response.entryId],
          refetchType: 'none',
        }),
        queryClient.invalidateQueries({
          queryKey: [KEYWORD_LOOKUP_QUERY_KEY],
        }),
      ]);
    },
  };
}

const guardedClients = new WeakSet<QueryClient>();
function guardMutationReplay(queryClient: QueryClient) {
  if (guardedClients.has(queryClient)) return;
  guardedClients.add(queryClient);
  const resume = queryClient.resumePausedMutations.bind(queryClient);
  // QueryClient itself resumes on focus/online before notifying queries. Return
  // promptly while unverified so /auth/me can refetch; leave mutations paused.
  // AuthProvider resumes them after verifying and filtering the owning identity.
  queryClient.resumePausedMutations = () => (isSessionVerified() ? resume() : Promise.resolve());
}

// Install before hydration so every automatic QueryClient replay is guarded.
export function registerEntryWorkspaceSaveMutationDefaults(queryClient: QueryClient) {
  guardMutationReplay(queryClient);
  queryClient.setMutationDefaults(
    ENTRY_WORKSPACE_SAVE_MUTATION_KEY,
    buildEntryWorkspaceSaveHandlers(queryClient),
  );
}

export function useEntryWorkspaceSave() {
  const queryClient = useQueryClient();
  guardMutationReplay(queryClient);
  const originatingOwner = getSessionOwner();

  const mutation = useMutation<ShiftbookEntryWorkspaceSaveResponse, Error, EntryWorkspaceSaveInput>(
    {
      mutationKey: ENTRY_WORKSPACE_SAVE_MUTATION_KEY,
      ...buildEntryWorkspaceSaveHandlers(queryClient),
    },
  );
  return {
    ...mutation,
    mutate: (input: EntryWorkspaceSaveInput, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ ...input, owner: input.owner ?? originatingOwner ?? undefined }, options),
    mutateAsync: (
      input: EntryWorkspaceSaveInput,
      options?: Parameters<typeof mutation.mutateAsync>[1],
    ) =>
      mutation.mutateAsync(
        { ...input, owner: input.owner ?? originatingOwner ?? undefined },
        options,
      ),
  };
}
